import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ChartBar, Trophy, CheckCircle, XCircle, Star } from '@phosphor-icons/react';
import { Spinner } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Per-class learning stats panel — displayed at the bottom of the Klassen tab
 * in the Teacher Dashboard. Aggregates correct/incorrect across the auto-graded
 * skills (Lesen/Hören/Vokabular/Grammatik/Spiele) and the teacher 1-10 ratings
 * for the manual ones (Sprechen/Schreiben). Only teachers see this — backend
 * already enforces the role gate.
 */
const ClassStatsBoard = ({ groups = [] }) => {
  // "__ALL__" is the cross-class aggregate sentinel the backend accepts.
  const [groupId, setGroupId] = useState('__ALL__');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/teacher/classes/${groupId}/stats`, { withCredentials: true });
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [groupId]);

  const accuracyColor = (acc) => {
    if (acc >= 80) return 'text-emerald-700 bg-emerald-50';
    if (acc >= 60) return 'text-amber-700 bg-amber-50';
    if (acc > 0)   return 'text-red-700 bg-red-50';
    return 'text-stone-500 bg-stone-50';
  };
  const scoreColor = (s) => {
    if (s == null) return 'text-stone-500 bg-stone-50';
    if (s >= 8) return 'text-emerald-700 bg-emerald-50';
    if (s >= 6) return 'text-amber-700 bg-amber-50';
    return 'text-red-700 bg-red-50';
  };

  return (
    <div className="bg-white rounded-2xl border border-[#D6D3D1] p-5 mt-6" data-testid="class-stats-board">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2">
          <ChartBar size={22} weight="fill" className="text-[#AB1C24]" />
          <h3 className="font-heading font-semibold text-[#292524] text-lg">
            Lernfortschritt pro Klasse
          </h3>
        </div>
        <select
          value={groupId}
          onChange={(e) => setGroupId(e.target.value)}
          className="text-sm border border-stone-300 rounded-md px-2 py-1.5 bg-white"
          data-testid="class-stats-group-select"
        >
          <option value="__ALL__">Alle Klassen kombiniert</option>
          {groups.map(g => (
            <option key={g.group_id} value={g.group_id}>
              {g.name}{g.level ? ` · ${g.level}` : ''}
            </option>
          ))}
        </select>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-[#78716C] py-6 justify-center">
          <Spinner size={16} className="animate-spin" /> Lade Statistiken…
        </div>
      )}

      {!loading && data && (
        <>
          <p className="text-xs text-[#78716C] mb-3">
            {data.member_count} Schüler insgesamt · Werte basieren auf automatisch
            ausgewerteten Übungen (Lesen / Hören / Vokabular / Grammatik / Spiele)
            und deinen 1–10-Bewertungen (Sprechen / Schreiben).
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.categories.map(c => (
              <div
                key={c.key}
                className="rounded-xl border border-stone-200 bg-[#FAF9F6] p-3"
                data-testid={`class-stats-cat-${c.key}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <p className="font-heading font-semibold text-[#292524]">{c.label}</p>
                  <span className="text-[10px] uppercase tracking-wider font-bold text-stone-500">
                    {c.type === 'manual' ? 'Lehrer-Bewertung' : 'Auto'}
                  </span>
                </div>
                {c.type === 'auto' ? (
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle size={14} className="text-emerald-600" weight="fill" />
                      <span className="text-[#292524] font-medium">{c.correct}</span>
                      <span className="text-[#78716C]">richtig</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <XCircle size={14} className="text-red-500" weight="fill" />
                      <span className="text-[#292524] font-medium">{c.incorrect}</span>
                      <span className="text-[#78716C]">falsch</span>
                    </div>
                    <div className={`mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${accuracyColor(c.accuracy)}`}>
                      {c.total > 0 ? `${c.accuracy}% Trefferquote` : 'Noch keine Daten'}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2 text-sm">
                      <Star size={14} className="text-amber-500" weight="fill" />
                      <span className="text-[#292524] font-medium">{c.graded_count}</span>
                      <span className="text-[#78716C]">bewertet</span>
                    </div>
                    <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${scoreColor(c.avg_score)}`}>
                      {c.avg_score != null ? `⌀ ${c.avg_score} / 10` : 'Noch keine Bewertung'}
                    </div>
                    {c.graded_count === 0 && (
                      <p className="text-[11px] text-[#A8A29E] italic">
                        Bewerte Abgaben mit 1–10 in {'„Materialien"'}.
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {data.top_students && data.top_students.length > 0 && (
            <div className="mt-5 bg-gradient-to-r from-amber-50 to-white border border-amber-200 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-2">
                <Trophy size={18} weight="fill" className="text-amber-600" />
                <p className="font-heading font-semibold text-[#292524] text-sm">Top 5 — meiste richtige Übungen</p>
              </div>
              <ol className="space-y-1">
                {data.top_students.map((s, i) => (
                  <li
                    key={s.user_id}
                    className="flex items-center justify-between text-sm"
                    data-testid={`class-stats-top-${i}`}
                  >
                    <span className="text-[#292524]">
                      <span className="text-amber-600 font-bold mr-2">{i + 1}.</span>{s.name}
                    </span>
                    <span className="text-[#78716C] font-medium">{s.correct} richtig</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </>
      )}

      {!loading && !data && (
        <p className="text-sm text-[#A8A29E] italic">Konnte Statistiken nicht laden.</p>
      )}
    </div>
  );
};

export default ClassStatsBoard;
